import React, { useState, useMemo } from "react";
import { ThemeProvider } from "styled-components";
import { ColumnsType } from "antd/es/table";
import { useTheme } from "../../../context/ThemeContext";
import Table from "../../../commonComponents/Table";
import TablePagination from "../../../commonComponents/Pagination";
import { TableCard, TableHeader, TableTitle, TableDescription } from "./styles";
import { SalesRankingTableProps, SalesRankingItem } from "./types";

const SalesRankingTable: React.FC<SalesRankingTableProps> = ({ data }) => {
  const { isDarkMode } = useTheme();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: "asc" | "desc";
  }>({
    key: null,
    direction: "asc",
  });

  const handlePaginationChange = (page: number, size: number) => {
    if (size && size !== pageSize) {
      setPageSize(size);
    }
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleSort = (columnKey: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === columnKey && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: columnKey, direction });
    setCurrentPage(1);
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    const sorted = [...data].sort((a, b) => {
      let aValue: any = a[sortConfig.key as keyof SalesRankingItem];
      let bValue: any = b[sortConfig.key as keyof SalesRankingItem];

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [data, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    return sortedData.slice(startIdx, endIdx);
  }, [sortedData, currentPage, pageSize]);

  const columns: ColumnsType<SalesRankingItem> = [
    {
      title: "Rank",
      key: "rank",
      width: 100,
      render: (_, __, index) => {
        const rank = (currentPage - 1) * pageSize + index + 1;
        const isFirst = rank === 1;
        return <span style={{ fontWeight: isFirst ? 700 : 400 }}>{rank}</span>;
      },
    },
    {
      title: "Item",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: sortConfig.key === "name" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
      onHeaderCell: () => ({
        onClick: () => handleSort("name"),
        style: { cursor: "pointer" },
      }),
      render: (name, __, index) => {
        const rank = (currentPage - 1) * pageSize + index + 1;
        const isFirst = rank === 1;
        return <span style={{ fontWeight: isFirst ? 700 : 400 }}>{name}</span>;
      },
    },
    {
      title: "Units Sold",
      dataIndex: "unitsSold",
      key: "unitsSold",
      align: "right",
      sorter: true,
      sortOrder: sortConfig.key === "unitsSold" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
      onHeaderCell: () => ({
        onClick: () => handleSort("unitsSold"),
        style: { cursor: "pointer" },
      }),
      render: (unitsSold, __, index) => {
        const rank = (currentPage - 1) * pageSize + index + 1;
        const isFirst = rank === 1;
        return (
          <span style={{ fontWeight: isFirst ? 700 : 400 }}>
            {unitsSold.toLocaleString()}
          </span>
        );
      },
    },
  ];

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? "dark" : "light" }}>
      <TableCard>
        <TableHeader>
          <TableTitle>Sales Ranking</TableTitle>
          <TableDescription>
            Items ranked by the number of units sold.
          </TableDescription>
        </TableHeader>
        <Table<SalesRankingItem>
          columns={columns}
          dataSource={paginatedData}
          rowKey="id"
          loading={false}
          pagination={false}
          emptyText="No sales ranking data available"
          emptyDescription="No items found for the selected period"
          scroll={paginatedData.length > 0 ? { x: true, y: 400 } : undefined}
          disableScroll={paginatedData.length === 0}
          headerBackgroundColor={isDarkMode ? "#0f172a" : "#f8fafc"}
          removeVerticalBorders={true}
          thPadding="16px"
          tdPadding="16px"
          firstChildBorderRadius="12px"
          border="0"
          borderBottomLeftRight={true}
          theme={isDarkMode ? "dark" : "light"}
        />
        <TablePagination
          totalCount={sortedData.length}
          currentPage={currentPage}
          pageSize={pageSize}
          onChange={handlePaginationChange}
          totalLabel="Total items"
          padding="24px 0 0 0"
        />
      </TableCard>
    </ThemeProvider>
  );
};

export default SalesRankingTable;
